import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { localDateSchema, moneySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
/**
 * Ödeme planlama (Prompt 11 §6; özellik bayrağı `EXPENSES_BUDGET`).
 *
 * Değişmezler:
 *  · Bir KAYNAK (açık kalem, çek, DBS kalemi, kredi taksiti, vergi yükümlülüğü, şirket kartı) aynı anda TEK
 *    etkin partide bekleyebilir (veritabanı tekil dizini) → çift ödeme engeli.
 *  · Parti onaylanmadan yürütülemez; yürütmede her satır KENDİ savepoint'indedir — bir satırın hatası
 *    diğerlerini geri almaz (`FAILED` + Türkçe gerekçe).
 *  · Banka dosyasındaki IBAN partide SAKLANMAZ, carinin kartından okunur; dosya ve IBAN loglanmaz.
 */
const versionSchema = z.number().int().min(1).describe("İyimser kilit sürümü");
const positiveMoneySchema = moneySchema.refine((value) => BigInt(value) > 0n, "Tutar sıfırdan büyük olmalıdır");
export const PAYMENT_SOURCE_TYPES = [
    "OPEN_ITEM",
    "INSTRUMENT",
    "DBS_ITEM",
    "LOAN_INSTALLMENT",
    "TAX_OBLIGATION",
    "COMPANY_CARD",
];
export const paymentSourceTypeSchema = z.enum(PAYMENT_SOURCE_TYPES);
export const PAYMENT_SOURCE_TYPE_LABELS = {
    OPEN_ITEM: "Tedarikçi açık kalemi",
    INSTRUMENT: "Verilen çek / senet",
    DBS_ITEM: "DBS kalemi",
    LOAN_INSTALLMENT: "Kredi taksiti",
    TAX_OBLIGATION: "Vergi / SGK yükümlülüğü",
    COMPANY_CARD: "Şirket kredi kartı borcu",
};
export const PAYMENT_BATCH_STATUSES = [
    "DRAFT",
    "PENDING_APPROVAL",
    "APPROVED",
    "EXECUTED",
    "REJECTED",
    "CANCELLED",
];
export const paymentBatchStatusSchema = z.enum(PAYMENT_BATCH_STATUSES);
export const PAYMENT_BATCH_STATUS_LABELS = {
    DRAFT: "Taslak",
    PENDING_APPROVAL: "Onay bekliyor",
    APPROVED: "Onaylandı",
    EXECUTED: "Yürütüldü",
    REJECTED: "Reddedildi",
    CANCELLED: "İptal edildi",
};
export const paymentBatchLineStatusSchema = z.enum(["PENDING", "PAID", "FAILED", "SKIPPED"]);
export const PAYMENT_BATCH_LINE_STATUS_LABELS = {
    PENDING: "Bekliyor",
    PAID: "Ödendi",
    FAILED: "Başarısız",
    SKIPPED: "Atlandı",
};
// ── Ödeme adayları ───────────────────────────────────────────────────────────
export const paymentCandidateSchema = z.object({
    sourceType: paymentSourceTypeSchema,
    sourceId: uuidSchema,
    /** Kişisel veri ve IBAN İÇERMEZ. */
    description: z.string(),
    counterpartyId: uuidSchema.nullable(),
    counterpartyName: z.string().nullable(),
    docNo: z.string().nullable(),
    dueDate: localDateSchema,
    /** Kuruş; tutarı henüz bilinmeyen vergi yükümlülüğünde null (partiye EKLENEMEZ). */
    remainingAmount: moneySchema.nullable(),
    /** Bugüne göre gecikme günü (0 = vadesi gelmemiş ya da bugün). */
    overdueDays: z.number().int(),
    /** Başka etkin partide bekliyorsa seçilemez. */
    inActiveBatch: z.boolean(),
    /** Bekliyorsa o partinin numarası (kullanıcıya gösterilir). */
    activeBatchNo: z.string().nullable(),
});
export const paymentCandidateListSchema = z.object({
    items: z.array(paymentCandidateSchema),
    totalAmount: moneySchema.describe("Seçilebilir (tutarı bilinen, partide olmayan) adayların toplamı"),
});
export const listPaymentCandidatesQuerySchema = z.object({
    companyId: uuidSchema,
    dueTo: localDateSchema.optional().describe("Bu tarihe kadar vadesi gelenler; varsayılan bugün + 30 gün"),
    types: z
        .string()
        .optional()
        .describe("Virgülle ayrılmış kaynak türleri (ör. OPEN_ITEM,TAX_OBLIGATION); boşsa hepsi"),
    counterpartyId: uuidSchema.optional(),
});
// ── Ödeme partisi ────────────────────────────────────────────────────────────
export const paymentBatchLineSchema = z.object({
    id: uuidSchema,
    lineNo: z.number().int(),
    sourceType: paymentSourceTypeSchema,
    sourceId: uuidSchema,
    counterpartyId: uuidSchema.nullable(),
    counterpartyName: z.string().nullable(),
    description: z.string(),
    dueDate: localDateSchema,
    amount: moneySchema,
    isActive: z.boolean(),
    status: paymentBatchLineStatusSchema,
    resultDocType: z.string().nullable(),
    resultDocId: uuidSchema.nullable(),
    errorMessage: z.string().nullable(),
});
export const paymentBatchSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    batchNo: z.string().describe("OP2026000007"),
    paymentDate: localDateSchema,
    bankAccountId: uuidSchema,
    bankAccountName: z.string().nullable(),
    description: z.string().nullable(),
    totalAmount: moneySchema,
    lineCount: z.number().int(),
    status: paymentBatchStatusSchema,
    approvalRequestId: uuidSchema.nullable(),
    fileGeneratedAt: timestampSchema.nullable(),
    executedAt: timestampSchema.nullable(),
    cancelReason: z.string().nullable(),
    lines: z.array(paymentBatchLineSchema),
    createdAt: timestampSchema,
    version: versionSchema,
});
export const paymentBatchPageSchema = pageOf(paymentBatchSchema.omit({ lines: true }));
export const listPaymentBatchesQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    status: paymentBatchStatusSchema.optional(),
    dateFrom: localDateSchema.optional(),
    dateTo: localDateSchema.optional(),
});
export const paymentBatchLineInputSchema = z.object({
    sourceType: paymentSourceTypeSchema,
    sourceId: uuidSchema,
    /** Boşsa kaynağın KALAN tutarının tamamı ödenir. */
    amount: positiveMoneySchema.nullable().default(null),
});
export const createPaymentBatchSchema = z.object({
    companyId: uuidSchema,
    paymentDate: localDateSchema,
    bankAccountId: uuidSchema,
    description: z.string().trim().max(500).nullable().default(null),
    lines: z.array(paymentBatchLineInputSchema).max(500).default([]),
});
export const updatePaymentBatchSchema = z.object({
    paymentDate: localDateSchema.optional(),
    bankAccountId: uuidSchema.optional(),
    description: z.string().trim().max(500).nullable().optional(),
    version: versionSchema,
});
export const addPaymentBatchLinesSchema = z.object({
    lines: z.array(paymentBatchLineInputSchema).min(1).max(500),
    version: versionSchema,
});
export const removePaymentBatchLineSchema = z.object({ version: versionSchema });
/**
 * Satırın TUTARINI değiştirir (yalnızca DRAFT partide). Adı bilerek `update…Schema` DEĞİLDİR: `amount` zorunlu
 * olduğu için `patch.test.ts`'in "gönderilmeyen alan çıktıda yer almaz" koruması bu şemaya uymaz.
 * Kaynak ve cari değişmez; tutar kaynağın kalanını aşamaz ve kısmi ödeme yalnızca açık kalemde geçerlidir.
 */
export const changePaymentBatchLineAmountSchema = z.object({
    amount: positiveMoneySchema,
    version: versionSchema,
});
export const submitPaymentBatchSchema = z.object({ version: versionSchema });
export const executePaymentBatchSchema = z.object({ version: versionSchema });
export const cancelPaymentBatchSchema = z.object({
    reason: z.string().trim().min(3).max(500),
    version: versionSchema,
});
// ── Banka toplu ödeme dosyası ────────────────────────────────────────────────
export const BANK_PAYMENT_FILE_FORMATS = ["CSV"];
export const bankPaymentFileFormatSchema = z.enum(BANK_PAYMENT_FILE_FORMATS);
export const bankPaymentFileQuerySchema = z.object({
    format: bankPaymentFileFormatSchema.default("CSV"),
});
/** Dosyaya GİREMEYEN satırlar (carinin IBAN'ı yok). IBAN yanıtta TAŞINMAZ. */
export const bankPaymentFileSkippedSchema = z.object({
    lineNo: z.number().int(),
    counterpartyId: uuidSchema.nullable(),
    counterpartyName: z.string().nullable(),
    reason: z.string(),
});
export const bankPaymentFilePreviewSchema = z.object({
    batchNo: z.string(),
    format: bankPaymentFileFormatSchema,
    fileName: z.string(),
    includedCount: z.number().int(),
    includedAmount: moneySchema,
    skipped: z.array(bankPaymentFileSkippedSchema),
});
//# sourceMappingURL=payments.js.map